/**
 * Features Page
 * =============
 * Detailed features showcase page with zig-zag layout and images.
 */

import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, ArrowRight, Brain, LayoutDashboard, Gift, ShoppingBag, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/shared/Button';

gsap.registerPlugin(ScrollTrigger);

/**
 * Features Page Component
 * Showcases DME-CPH features with zig-zag layout and images.
 * 
 * @returns {JSX.Element} Features page
 */
function Features() {
  const sectionsRef = useRef([]);

  // Feature data with images
  const features = [
    {
      id: 'prediction',
      icon: <Brain className="w-8 h-8" />,
      badge: '91.8% Accurate',
      title: 'Commitment Prediction Engine',
      description: 'Our AI analyzes student behavior across every touchpoint to calculate a Commitment Score for each accepted student. Know who will enroll and who won\'t—months before the deadline.',
      benefits: [
        'Real-time commitment scores updated as students engage',
        'Risk indicators flag at-risk students automatically',
        'Behavior pattern analysis beyond simple metrics',
        '91.8% prediction accuracy',
      ],
      image: '/assets/images/gg_Image_bdr919bdr919bdr9.png',
      imageAlt: 'AI Prediction Dashboard',
      color: 'orange',
    },
    {
      id: 'engagement',
      icon: <LayoutDashboard className="w-8 h-8" />,
      badge: 'Voice • SMS • Chat',
      title: 'Multi-Channel Engagement',
      description: 'Meet students wherever they are. Our AI voice agent ADDI conducts personalized outreach calls, answers questions, and routes complex inquiries to your team.',
      benefits: [
        'AI-powered voice agent for calls',
        'Two-way SMS with smart automation',
        'Chat interface for web support',
        'Seamless handoff to human counselors',
      ],
      image: '/assets/images/student_mobile_engagement_1769976387738.png',
      imageAlt: 'Student Mobile Engagement',
      color: 'blue',
    },
    {
      id: 'acceptance',
      icon: <Gift className="w-8 h-8" />,
      badge: 'Physical + Digital',
      title: 'Acceptance Kit Integration',
      description: 'Students who receive personalized Acceptance Kits show dramatically higher engagement. Trigger kit fulfillment based on student behavior and track engagement from unboxing to enrollment.',
      benefits: [
        'Automated triggers based on commitment stage',
        'Gift certificate redemption tracking',
        'Sentiment capture during kit experience',
        '+47% engagement boost',
      ],
      image: '/assets/images/gg_Image_904ox6904.png',
      imageAlt: 'Acceptance Kit Box',
      color: 'green',
    },
    {
      id: 'mallsystem',
      icon: <ShoppingBag className="w-8 h-8" />,
      badge: 'Incentives That Drive Action',
      title: 'MallSystem Connection',
      description: 'Connected to DME\'s MallSystem, offer students branded merchandise and incentive gifts—all while capturing valuable engagement data.',
      benefits: [
        'Branded merchandise selection',
        'Gift-based commitment confirmation',
        'Engagement data from selection process',
        'Stetson-branded student store',
      ],
      image: '/assets/images/gg_Image_4vbc1n4vbc1n4vbc.png',
      imageAlt: 'MallSystem Interface',
      color: 'orange',
    },
    {
      id: 'analytics',
      icon: <BarChart3 className="w-8 h-8" />,
      badge: 'Complete Visibility',
      title: 'Analytics Dashboard',
      description: 'A single dashboard shows everything: commitment scores, engagement trends, intervention effectiveness, and predicted final yield. No more cobbling together reports.',
      benefits: [
        'Class-wide commitment forecasting',
        'Individual student engagement timelines',
        'Exportable reports for leadership',
        'Real-time yield predictions',
      ],
      image: '/assets/images/gg_Image_uil2veuil2veuil2.png',
      imageAlt: 'Analytics Dashboard',
      color: 'blue',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate each feature section
      sectionsRef.current.forEach((section, index) => {
        if (!section) return;

        const image = section.querySelector('.feature-image');
        const content = section.querySelector('.feature-content');
        const isReversed = index % 2 === 1;

        // Image animation
        gsap.fromTo(image,
          { 
            opacity: 0, 
            x: isReversed ? 50 : -50,
            scale: 0.95 
          },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          }
        );

        // Content animation
        gsap.fromTo(content,
          { 
            opacity: 0, 
            x: isReversed ? -50 : 50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
            delay: 0.1,
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  const getColorClasses = (color) => {
    switch (color) {
      case 'orange':
        return {
          bg: 'bg-dme-orange-50',
          text: 'text-dme-orange',
          badge: 'bg-dme-orange/10 text-dme-orange',
          check: 'text-dme-orange',
        };
      case 'green':
        return {
          bg: 'bg-dme-green-50',
          text: 'text-dme-green',
          badge: 'bg-dme-green/10 text-dme-green',
          check: 'text-dme-green',
        };
      default:
        return {
          bg: 'bg-dme-blue-50',
          text: 'text-dme-blue',
          badge: 'bg-dme-blue/10 text-dme-blue',
          check: 'text-dme-blue',
        };
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              Powerful Capabilities
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Five Powerful Features
            </h1>
            <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
              From AI-powered prediction to multi-channel engagement, the Commitment Predictor Hub 
              gives you a complete toolkit for enrollment success.
            </p>
          </div>
        </div>
      </section>

      {/* Features - Zig Zag Layout */}
      <section className="py-20 lg:py-32 bg-gray-50">
        <div className="container-dme">
          <div className="space-y-24 lg:space-y-32">
            {features.map((feature, index) => {
              const colors = getColorClasses(feature.color);
              const isReversed = index % 2 === 1;

              return (
                <div
                  key={feature.id}
                  ref={(el) => { sectionsRef.current[index] = el; }}
                  className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}
                >
                  {/* Image */}
                  <div className="feature-image lg:w-1/2 w-full">
                    <div className="relative group">
                      {/* Decorative blur */}
                      <div className={`absolute -inset-4 bg-gradient-to-r ${
                        feature.color === 'orange' ? 'from-dme-orange/20 to-dme-blue/20' :
                        feature.color === 'green' ? 'from-dme-green/20 to-dme-blue/20' :
                        'from-dme-blue/20 to-dme-orange/20'
                      } rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`} />
                      
                      {/* Image container */}
                      <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 aspect-[4/3]">
                        <img
                          src={feature.image}
                          alt={feature.imageAlt}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/images/gg_Image_bdr919bdr919bdr9.png';
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`feature-content lg:w-1/2 w-full ${isReversed ? 'lg:text-right' : ''}`}>
                    {/* Icon & Badge */}
                    <div className={`flex items-center gap-4 mb-6 ${isReversed ? 'lg:justify-end' : ''}`}>
                      <div className={`w-16 h-16 rounded-2xl ${colors.bg} flex items-center justify-center ${colors.text}`}>
                        {feature.icon}
                      </div>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}>
                        {feature.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-heading font-bold text-3xl lg:text-4xl text-dme-blue-800 mb-4">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-lg text-dme-gray-600 leading-relaxed mb-6">
                      {feature.description}
                    </p>

                    {/* Benefits */}
                    <ul className={`space-y-3 ${isReversed ? 'lg:ml-auto' : ''}`}>
                      {feature.benefits.map((benefit, i) => (
                        <li key={i} className={`flex items-start gap-3 ${isReversed ? 'lg:flex-row-reverse lg:text-right' : ''}`}>
                          <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colors.check}`} />
                          <span className="text-dme-gray-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-dme-blue-50">
        <div className="container-dme">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-dme-blue-800 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-dme-gray-600 mb-8">
              See these features in action with a personalized demo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  View Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" size="lg">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Features;
